import React, { useEffect, useState, useCallback, useMemo } from 'react';
import rrService from '../services/rrService';
import SortableResizableTable from '../components/SortableResizableTable';
import RebalancerHistoryCharts from '../components/RebalancerHistoryCharts';
import AccountDetailsModal from '../components/AccountDetailsModal';
import PortfolioSummaryPieChart from '../components/PortfolioSummaryPieChart';
import CashSummaryCard from '../components/CashSummaryCard';
import SuggestedTradesTable from '../components/SuggestedTradesTable';
import RefreshPricesButton from '../components/RefreshPricesButton';
import RestrictedBuysNotice from '../components/RestrictedBuysNotice';
import AllocationVisualization from '../components/AllocationVisualization';
import AccountAllocationBars from '../components/AccountAllocationBars';
import RebalancingSummaryCards from '../components/RebalancingSummaryCards';
import '../styles/SelectStyles.css';

/**
 * RebalancerDashboard - Displays summary of daily rebalancer runs.
 * 
 * Shows three tables:
 * 1. Raise Cash Only - Account Families that need to sell to raise cash
 * 2. Invest Cash Only - Account Families that need to buy to invest excess cash
 * 3. Mixed - Account Families with both raise and invest needs (requires manual review)
 */
const RebalancerDashboard = () => {
  // State for each category
  const [raiseCashSummary, setRaiseCashSummary] = useState(null);
  const [investCashSummary, setInvestCashSummary] = useState(null);
  const [mixedSummary, setMixedSummary] = useState(null);
  const [noActionSummary, setNoActionSummary] = useState(null);

  const [isLoading, setIsLoading] = useState(true);
  const [errors, setErrors] = useState({ raiseCash: '', investCash: '', mixed: '', noAction: '' });

  // Account Family details cached by category
  const [clientDetails, setClientDetails] = useState({
    raiseCash: {},
    investCash: {},
    mixed: {},
    noAction: {}
  });

  const [loadingClients, setLoadingClients] = useState(new Set());

  // Active tab state
  const [activeTab, setActiveTab] = useState('raiseCash');

  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  // State for cash reserve details modal
  const [showCashReserveModal, setShowCashReserveModal] = useState(false);
  const [selectedAccountForDetails, setSelectedAccountForDetails] = useState(null);

  // State for account family detail modal
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedGroupForDetail, setSelectedGroupForDetail] = useState(null);
  const [selectedGroupCategory, setSelectedGroupCategory] = useState(null);

  // Strategy selection state for raise cash trades
  const [sellStrategy, setSellStrategy] = useState('tax_efficient'); // 'tax_efficient', 'weight_optimized', or 'isaac'

  // Margin mode selection for both raise cash and invest cash
  const [marginMode, setMarginMode] = useState('no_margin'); // 'no_margin' or 'with_margin'
  const [ignoreMargin, setIgnoreMargin] = useState(true); // noAction modal: ignore margin in cash/reserve calcs

  // Holdings for all accounts (keyed by account number)
  const [allAccountHoldings, setAllAccountHoldings] = useState({});
  const [isLoadingHoldings, setIsLoadingHoldings] = useState(false);

  // Shared view type for all allocation bars (Group Allocation widget + Account cards)
  const [allocViewType, setAllocViewType] = useState('sleeve');

  // Queued trades state - map of group_name -> queued trade data
  const [queuedTrades, setQueuedTrades] = useState({});
  const [isQueuingTrades, setIsQueuingTrades] = useState(false);
  // Allocation overrides computed by SuggestedTradesTable when trades are edited
  const [allocationOverrides, setAllocationOverrides] = useState({});
  // Live flat trade list from SuggestedTradesTable (updated on every edit)
  const [liveTradesOverrides, setLiveTradesOverrides] = useState({});

  // Toggle for showing queued vs suggested trades (when queued available)
  const [showQueuedTrades, setShowQueuedTrades] = useState(true);

  // State for bulk order download
  const [isDownloadingOrders, setIsDownloadingOrders] = useState(false);
  const [queuedTradesSummary, setQueuedTradesSummary] = useState(null);
  const [selectedQueuedGroups, setSelectedQueuedGroups] = useState(new Set());
  const [hiddenGroups, setHiddenGroups] = useState(new Set());

  // State for rebalancer constants (cash-like symbols, etc.)
  const [rebalancerConstants, setRebalancerConstants] = useState(null);

  // Ref to store the queue trades trigger function from SuggestedTradesTable
  const queueTradesTriggerRef = React.useRef(null);

  /**
   * Open the cash reserve details modal for a specific account.
   * 
   * @param {string} accountNumber - Account number to show details for
   * @param {string} accountName - Account name for display
   */
  const openCashReserveDetails = (accountNumber, accountName) => {
    setSelectedAccountForDetails({ accountNumber, accountName });
    setShowCashReserveModal(true);
  };

  /**
   * Close the cash reserve details modal.
   */
  const closeCashReserveDetails = () => {
    setShowCashReserveModal(false);
    setSelectedAccountForDetails(null);
  };

  const fetchClientDetail = useCallback(async (groupName, category, fetchFn) => {
    // Already have this account family's data
    if (clientDetails[category]?.[groupName]) return;

    // Mark as loading
    setLoadingClients(prev => new Set(prev).add(`${category}:${groupName}`));

    try {
      const data = await fetchFn(groupName);
      setClientDetails(prev => ({
        ...prev,
        [category]: {
          ...prev[category],
          [groupName]: data
        }
      }));
    } catch (err) {
      console.error(`Error fetching detail for ${groupName}:`, err);
      setClientDetails(prev => ({
        ...prev,
        [category]: {
          ...prev[category],
          [groupName]: { error: err.response?.data?.detail || `Failed to load details for ${groupName}` }
        }
      }));
    } finally {
      setLoadingClients(prev => {
        const newSet = new Set(prev);
        newSet.delete(`${category}:${groupName}`);
        return newSet;
      });
    }
  }, [clientDetails]);

  /**
   * Apply the result of a real-time Buy-to-Max rerun directly to the
   * cached client detail (no S3 round trip — the endpoint returns the
   * full rebalance result synchronously).
   *
   * @param {string} groupName - The account family / group name
   * @param {string} category - Category: 'raiseCash', 'investCash', 'mixed', or 'noAction'
   * @param {object} result - Full rebalance result from the buy-to-max endpoint
   */
  const applyBuyToMaxResult = useCallback((groupName, category, result) => {
    if (!result) return;
    setClientDetails(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [groupName]: result
      }
    }));
    // Clear any stale edits/overrides from the previous result
    setLiveTradesOverrides(prev => {
      const next = { ...prev };
      delete next[groupName];
      return next;
    });
    setAllocationOverrides(prev => {
      const next = { ...prev };
      delete next[groupName];
      return next;
    });
  }, []);

  /**
   * Open the account family detail modal.
   * 
   * @param {object} row - The row data (group)
   * @param {string} category - Category: 'raiseCash', 'investCash', or 'mixed'
   */
  const openDetailModal = useCallback((row, category) => {
    const groupName = row.name;
    setSelectedGroupForDetail(row);
    setSelectedGroupCategory(category);
    setShowDetailModal(true);
    setIgnoreMargin(true); // Reset ignore-margin toggle for each new modal open

    // Fetch detail if not already cached
    let fetchFn;
    if (category === 'raiseCash') {
      fetchFn = rrService.getDailyRaiseCashClientDetail;
    } else if (category === 'investCash') {
      fetchFn = rrService.getDailyInvestCashClientDetail;
    } else if (category === 'mixed') {
      fetchFn = rrService.getDailyMixedClientDetail;
    } else if (category === 'noAction') {
      fetchFn = rrService.getDailyNoActionClientDetail;
    }

    if (fetchFn && !clientDetails[category]?.[groupName]) {
      fetchClientDetail(groupName, category, fetchFn);
    }
  }, [clientDetails, fetchClientDetail]);

  /**
   * Close the account family detail modal.
   */
  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedGroupForDetail(null);
    setSelectedGroupCategory(null);
    setAllAccountHoldings({}); // Clear holdings when closing modal
    setMarginMode('no_margin'); // Reset margin toggle for next open
    setIgnoreMargin(true);      // Reset ignore-margin toggle for next open
    setLiveTradesOverrides({});  // Clear live trade overrides on close
  };

  /**
   * Handle queuing trades for the current group.
   * Collects trades from SuggestedTradesTable and saves to S3.
   */
  const handleQueueTrades = async (trades, accountAllocations, securityAllocations) => {
    if (!selectedGroupForDetail || !selectedGroupCategory) return;

    const groupName = selectedGroupForDetail.name;

    // Strip zero-impact items to reduce queued file size
    const trimmedAccountAllocations = (accountAllocations || [])
      .filter(acct => acct.tradesCount > 0)
      .map(acct => ({
        ...acct,
        securities: (acct.securities || []).filter(s => s.tradeImpact !== 0)
      }));
    const trimmedSecurityAllocations = (securityAllocations || [])
      .filter(s => s.tradeImpact !== 0);

    // Always queue as 'Custom' — only the currently selected/edited trades are queued,
    // not all strategies (tax, weight, isaac). The base category and strategy are stored
    // as metadata for reference only.
    const queueCategory = 'Custom';

    setIsQueuingTrades(true);
    try {
      const result = await rrService.queueTrades(
        groupName,
        queueCategory,
        trades,
        trimmedAccountAllocations,
        trimmedSecurityAllocations
      );

      // Update local state with the new queued trade
      setQueuedTrades(prev => ({
        ...prev,
        [groupName]: {
          group_name: groupName,
          category: queueCategory,
          trades: trades,
          account_allocations: accountAllocations,
          security_allocations: securityAllocations,
          queued_at: result.queued_at,
          status: 'queued'
        }
      }));

      // Refresh the summary to update custodian counts
      fetchQueuedTradesSummary();

      // Close the modal
      closeDetailModal();
    } catch (error) {
      console.error('Error queuing trades:', error);
      alert(`Error queuing trades: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsQueuingTrades(false);
    }
  };

  /**
   * Handle clearing/un-queuing trades for a group.
   */
  const handleClearQueuedTrades = async (groupName) => {
    try {
      await rrService.deleteQueuedTrades(groupName);
      setQueuedTrades(prev => {
        const newQueued = { ...prev };
        delete newQueued[groupName];
        return newQueued;
      });
      // Remove from any selection and refresh the summary
      setSelectedQueuedGroups(prev => { const n = new Set(prev); n.delete(groupName); return n; });
      // Refresh the summary
      fetchQueuedTradesSummary();
    } catch (error) {
      console.error('Error clearing queued trades:', error);
      alert(`Error clearing queued trades: ${error.response?.data?.detail || error.message}`);
    }
  };

  /**
   * Fetch the queued trades summary (counts by custodian).
   */
  const fetchQueuedTradesSummary = async () => {
    try {
      const summary = await rrService.getQueuedTradesSummary();
      setQueuedTradesSummary(summary);
    } catch (error) {
      console.error('Error fetching queued trades summary:', error);
      setQueuedTradesSummary(null);
    }
  };

  /**
   * Handle downloading bulk order files for all queued trades.
   */
  const handleDownloadBulkOrders = async () => {
    setIsDownloadingOrders(true);
    try {
      const blob = await rrService.generateBulkOrders();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Extract filename from content-disposition if available, or use default
      const today = new Date().toISOString().split('T')[0];
      link.download = `Order_Files_${today}.zip`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading bulk orders:', error);
      alert(`Error downloading order files: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsDownloadingOrders(false);
    }
  };

  const toggleSelectedQueuedGroup = (groupName) => {
    setSelectedQueuedGroups(prev => {
      const n = new Set(prev);
      if (n.has(groupName)) n.delete(groupName); else n.add(groupName);
      return n;
    });
  };

  const toggleHideGroup = (groupName) => {
    setHiddenGroups(prev => {
      const n = new Set(prev);
      if (n.has(groupName)) n.delete(groupName); else n.add(groupName);
      return n;
    });
  };

  const reorderWithHidden = (arr) => {
    if (!arr || !Array.isArray(arr)) return arr;
    const notHidden = arr.filter(item => !hiddenGroups.has(item.name));
    const hidden = arr.filter(item => hiddenGroups.has(item.name));
    return [...notHidden, ...hidden];
  };

  const handleDownloadSelectedQueuedOrders = async () => {
    if (!selectedQueuedGroups || selectedQueuedGroups.size === 0) return;
    setIsDownloadingOrders(true);
    try {
      const groupNames = Array.from(selectedQueuedGroups);
      const result = await rrService.generateSelectedOrders(groupNames);

      // Download the file
      const url = window.URL.createObjectURL(result.blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = result.filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      alert(`Downloaded order file(s) for ${groupNames.length} group(s).`);
    } catch (err) {
      console.error('Error downloading selected queued orders', err);
      alert(`Error downloading orders: ${err.response?.data?.detail || err.message}`);
    } finally {
      setIsDownloadingOrders(false);
    }
  };

  // Reset ignore-margin toggle whenever a new group is opened
  useEffect(() => {
    setIgnoreMargin(true);
  }, [selectedGroupForDetail]);

  // Fetch holdings for all accounts when client detail is loaded
  useEffect(() => {
    const fetchHoldingsForAccounts = async () => {
      if (!selectedGroupForDetail || !selectedGroupCategory) return;

      // Clear stale data and show loading state immediately
      setAllAccountHoldings({});
      setIsLoadingHoldings(true);

      const detail = clientDetails[selectedGroupCategory]?.[selectedGroupForDetail.name];
      if (!detail || detail.error) {
        setIsLoadingHoldings(false);
        return;
      }

      // For noAction groups the detail IS directly the account snapshot
      if (selectedGroupCategory === 'noAction') {
        const accountAllocations = detail?.account_allocations || [];
        if (accountAllocations.length === 0) {
          setIsLoadingHoldings(false);
          return;
        }
        const holdingsMap = {};
        await Promise.all(
          accountAllocations.map(async (account) => {
            try {
              const response = await rrService.getAccountHoldings(account.accountNumber);
              holdingsMap[account.accountNumber] = response?.holdings || [];
            } catch (err) {
              holdingsMap[account.accountNumber] = [];
            }
          })
        );
        setAllAccountHoldings(holdingsMap);
        setIsLoadingHoldings(false);
        return;
      }

      // Get account allocations from detail (respecting margin mode)
      const isMixed = selectedGroupCategory === 'mixed';
      let raiseCashData = null;
      if (isMixed) {
        const rawRaiseCash = marginMode === 'with_margin'
          ? (detail.raise_cash_margin_result || detail.raise_cash_result)
          : detail.raise_cash_result;
        if (rawRaiseCash?.strategies) {
          raiseCashData = rawRaiseCash.strategies[sellStrategy] || rawRaiseCash.strategies['tax_efficient'];
        } else {
          raiseCashData = rawRaiseCash;
        }
      } else if (selectedGroupCategory === 'raiseCash') {
        const rawRaiseCash = marginMode === 'with_margin'
          ? (detail.raise_cash_margin_result || detail.raise_cash_result || detail)
          : (detail.raise_cash_result || detail);
        if (rawRaiseCash?.strategies) {
          raiseCashData = rawRaiseCash.strategies[sellStrategy] || rawRaiseCash.strategies['tax_efficient'];
        } else if (detail?.strategies) {
          raiseCashData = detail.strategies[sellStrategy] || detail.strategies['tax_efficient'];
        } else {
          raiseCashData = rawRaiseCash;
        }
      }
      const investCashData = isMixed
        ? (marginMode === 'with_margin'
          ? (detail.invest_cash_margin_result || detail.invest_cash_result)
          : detail.invest_cash_result)
        : (selectedGroupCategory === 'investCash'
          ? (marginMode === 'with_margin'
            ? (detail.invest_cash_margin_result || detail.invest_cash_result || detail)
            : (detail.invest_cash_result || detail))
          : null);
      const accountAllocations = raiseCashData?.account_allocations || investCashData?.account_allocations || [];

      if (accountAllocations.length === 0) {
        setIsLoadingHoldings(false);
        return;
      }

      // Fetch holdings for each account
      const holdingsMap = {};
      await Promise.all(
        accountAllocations.map(async (account) => {
          try {
            const response = await rrService.getAccountHoldings(account.accountNumber);
            holdingsMap[account.accountNumber] = response?.holdings || [];
          } catch (err) {
            console.error(`Error fetching holdings for ${account.accountNumber}:`, err);
            holdingsMap[account.accountNumber] = [];
          }
        })
      );

      setAllAccountHoldings(holdingsMap);
      setIsLoadingHoldings(false);
    };

    fetchHoldingsForAccounts();
  }, [selectedGroupForDetail, selectedGroupCategory, clientDetails, sellStrategy, marginMode]);

  // Fetch rebalancer constants on mount
  useEffect(() => {
    const fetchConstants = async () => {
      const constants = await rrService.getConstants();
      setRebalancerConstants(constants);
    };
    fetchConstants();
  }, []);

  // Fetch all summaries on mount
  useEffect(() => {
    fetchAllSummaries();
    fetchQueuedTrades();
    fetchQueuedTradesSummary();
  }, []);

  const fetchQueuedTrades = async () => {
    try {
      const result = await rrService.getAllQueuedTrades();
      setQueuedTrades(result.queued_trades || {});
      setSelectedQueuedGroups(new Set());
      // Also refresh the summary when queued trades change
      fetchQueuedTradesSummary();
    } catch (error) {
      console.error('Error fetching queued trades:', error);
      // Don't show error to user - queued trades are optional
      setQueuedTrades({});
    }
  };

  const fetchAllSummaries = async () => {
    setIsLoading(true);
    setErrors({ raiseCash: '', investCash: '', mixed: '', noAction: '' });

    // Fetch all four summaries in parallel
    const results = await Promise.allSettled([
      rrService.getDailyRaiseCashSummary(),
      rrService.getDailyInvestCashSummary(),
      rrService.getDailyMixedSummary(),
      rrService.getDailyNoActionSummary()
    ]);

    // Process raise cash result
    if (results[0].status === 'fulfilled') {
      setRaiseCashSummary(results[0].value);
    } else {
      const err = results[0].reason;
      if (err.response?.status !== 404) {
        setErrors(prev => ({ ...prev, raiseCash: err.response?.data?.detail || 'Failed to load raise cash summary' }));
      }
    }

    // Process invest cash result
    if (results[1].status === 'fulfilled') {
      setInvestCashSummary(results[1].value);
    } else {
      const err = results[1].reason;
      if (err.response?.status !== 404) {
        setErrors(prev => ({ ...prev, investCash: err.response?.data?.detail || 'Failed to load invest cash summary' }));
      }
    }

    // Process mixed result
    if (results[2].status === 'fulfilled') {
      setMixedSummary(results[2].value);
    } else {
      const err = results[2].reason;
      if (err.response?.status !== 404) {
        setErrors(prev => ({ ...prev, mixed: err.response?.data?.detail || 'Failed to load mixed summary' }));
      }
    }

    // Process no-action result
    if (results[3].status === 'fulfilled') {
      setNoActionSummary(results[3].value);
    } else {
      const err = results[3].reason;
      if (err.response?.status !== 404) {
        setErrors(prev => ({ ...prev, noAction: err.response?.data?.detail || 'Failed to load no-action summary' }));
      }
    }

    setIsLoading(false);
  };

  // Format currency values
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Format percentage
  const formatPercent = (value) => {
    if (value === null || value === undefined) return '-';
    return `${(value * 100).toFixed(1)}%`;
  };

  // Format timestamp (assumes UTC input, displays in local time)
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    try {
      let utcTimestamp = timestamp;
      if (!timestamp.endsWith('Z') && !timestamp.includes('+') && !timestamp.includes('-', 10)) {
        utcTimestamp = timestamp + 'Z';
      }
      const date = new Date(utcTimestamp);
      return date.toLocaleString();
    } catch {
      return timestamp;
    }
  };

  // Summary table columns for raise/invest cash
  const summaryColumns = [
    {
      key: 'queued_status',
      label: '',
      width: 50,
      minWidth: 50,
      maxWidth: 50,
      sortable: false,
      format: (value, row) => {
        const q = queuedTrades[row.name];
        if (!q) return null;
        const isSelected = selectedQueuedGroups.has(row.name);
        return (
          <button
            className={`btn btn-sm ${isSelected ? 'btn-primary' : 'btn-outline-secondary'}`}
            title={isSelected ? `Selected for download` : `Queued at ${new Date(q.queued_at).toLocaleString()}`}
            onClick={(e) => { e.stopPropagation(); toggleSelectedQueuedGroup(row.name); }}
            style={{ padding: '4px 8px', lineHeight: 1, fontSize: '0.95rem', minWidth: 40 }}
          >
            <i className={isSelected ? 'bi bi-check-circle-fill' : 'bi bi-clock-fill'}></i>
          </button>
        );
      }
    },
    { key: 'name', label: 'Account Family', width: 300, sortable: true },
    { key: 'trades', label: 'Trades', width: 80, sortable: true, align: 'center' },
    { key: 'value', label: 'Trade Value', width: 120, sortable: true, align: 'right', format: (value) => formatCurrency(value) },
    { key: 'days_in_state', label: 'Days', width: 70, sortable: true, align: 'center', format: (value) => value || 1 },
    {
      key: 'queued_action',
      label: '',
      width: 80,
      minWidth: 80,
      maxWidth: 80,
      sortable: false,
      format: (value, row) => {
        const q = queuedTrades[row.name];
        return (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
            {/* Hide/Ignore button always available */}
            <button
              className={`btn btn-sm ${hiddenGroups.has(row.name) ? 'btn-secondary' : 'btn-outline-secondary'}`}
              title={hiddenGroups.has(row.name) ? 'Hidden - click to unhide' : 'Hide this account (move to bottom)'}
              onClick={(e) => { e.stopPropagation(); toggleHideGroup(row.name); }}
              style={{ padding: '4px 8px', lineHeight: 1, fontSize: '0.95rem' }}
            >
              <i className={hiddenGroups.has(row.name) ? 'bi bi-eye-slash-fill' : 'bi bi-eye-slash'}></i>
            </button>
            {q && (
              <button
                className="btn btn-sm btn-outline-danger"
                title="Clear queued trades"
                onClick={(e) => { e.stopPropagation(); if (window.confirm(`Clear queued trades for ${row.name}?`)) { handleClearQueuedTrades(row.name); } }}
                style={{ padding: '4px 8px', lineHeight: 1, fontSize: '0.95rem' }}
              >
                <i className="bi bi-trash"></i>
              </button>
            )}
          </div>
        );
      }
    }
  ];

  // Summary table columns for mixed
  const mixedColumns = [
    {
      key: 'queued_status',
      label: '',
      width: 50,
      minWidth: 50,
      maxWidth: 50,
      sortable: false,
      format: (value, row) => {
        const q = queuedTrades[row.name];
        if (!q) return null;
        const isSelected = selectedQueuedGroups.has(row.name);
        return (
          <button
            className={`btn btn-sm ${isSelected ? 'btn-primary' : 'btn-outline-secondary'}`}
            title={isSelected ? `Selected for download` : `Queued at ${new Date(q.queued_at).toLocaleString()}`}
            onClick={(e) => { e.stopPropagation(); toggleSelectedQueuedGroup(row.name); }}
            style={{ padding: '4px 8px', lineHeight: 1, fontSize: '0.95rem', minWidth: 40 }}
          >
            <i className={isSelected ? 'bi bi-check-circle-fill' : 'bi bi-clock-fill'}></i>
          </button>
        );
      }
    },
    { key: 'name', label: 'Account Family', width: 250, sortable: true },
    { key: 'accounts_need_raise', label: 'Raise Accounts', width: 110, sortable: true, align: 'center' },
    { key: 'accounts_need_invest', label: 'Invest Accounts', width: 110, sortable: true, align: 'center' },
    { key: 'raise_cash_trades', label: 'Sell Trades', width: 90, sortable: true, align: 'center' },
    { key: 'raise_cash_value', label: 'Sell Value', width: 110, sortable: true, align: 'right', format: (value) => formatCurrency(value) },
    { key: 'invest_cash_trades', label: 'Buy Trades', width: 90, sortable: true, align: 'center' },
    { key: 'invest_cash_value', label: 'Buy Value', width: 110, sortable: true, align: 'right', format: (value) => formatCurrency(value) },
    { key: 'days_in_state', label: 'Days', width: 60, sortable: true, align: 'center', format: (value) => value || 1 },
    {
      key: 'queued_action',
      label: '',
      width: 80,
      minWidth: 80,
      maxWidth: 80,
      sortable: false,
      format: (value, row) => {
        const q = queuedTrades[row.name];
        return (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
            {/* Hide/Ignore button always available */}
            <button
              className={`btn btn-sm ${hiddenGroups.has(row.name) ? 'btn-secondary' : 'btn-outline-secondary'}`}
              title={hiddenGroups.has(row.name) ? 'Hidden - click to unhide' : 'Hide this account (move to bottom)'}
              onClick={(e) => { e.stopPropagation(); toggleHideGroup(row.name); }}
              style={{ padding: '4px 8px', lineHeight: 1, fontSize: '0.95rem' }}
            >
              <i className={hiddenGroups.has(row.name) ? 'bi bi-eye-slash-fill' : 'bi bi-eye-slash'}></i>
            </button>
            {q && (
              <button
                className="btn btn-sm btn-outline-danger"
                title="Clear queued trades"
                onClick={(e) => { e.stopPropagation(); if (window.confirm(`Clear queued trades for ${row.name}?`)) { handleClearQueuedTrades(row.name); } }}
                style={{ padding: '4px 8px', lineHeight: 1, fontSize: '0.95rem' }}
              >
                <i className="bi bi-trash"></i>
              </button>
            )}
          </div>
        );
      }
    }
  ];

  // Create render detail content function for a category
  const renderDetailContent = useCallback((category, tradeType = 'sell') => {
    const row = selectedGroupForDetail;
    if (!row) return null;

    const groupName = row.name;
    const detail = clientDetails[category]?.[groupName];
    const isLoadingThis = loadingClients.has(`${category}:${groupName}`);

    if (isLoadingThis) {
      return (
        <div className="p-4 bg-light">
          <div className="d-flex align-items-center">
            <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            Loading details...
          </div>
        </div>
      );
    }

    if (!detail) {
      return (
        <div className="p-4 bg-light text-muted">
          No details available
        </div>
      );
    }

    if (detail.error) {
      return (
        <div className="p-4 bg-light">
          <div className="alert alert-danger mb-0">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {detail.error}
          </div>
        </div>
      );
    }

    // For mixed category, detail contains raise_cash_result and invest_cash_result
    const isMixed = category === 'mixed';

    // Get raise cash data - respects both strategy and margin mode
    let raiseCashData = null;
    if (isMixed) {
      const rawRaiseCash = marginMode === 'with_margin'
        ? (detail.raise_cash_margin_result || detail.raise_cash_result)
        : detail.raise_cash_result;
      if (rawRaiseCash?.strategies) {
        raiseCashData = rawRaiseCash.strategies[sellStrategy] || rawRaiseCash.strategies['tax_efficient'];
      } else {
        raiseCashData = rawRaiseCash;
      }
    } else if (tradeType === 'sell') {
      const rawRaiseCash = marginMode === 'with_margin'
        ? (detail.raise_cash_margin_result || detail.raise_cash_result || detail)
        : (detail.raise_cash_result || detail);
      if (rawRaiseCash?.strategies) {
        raiseCashData = rawRaiseCash.strategies[sellStrategy] || rawRaiseCash.strategies['tax_efficient'];
      } else if (detail?.strategies) {
        raiseCashData = detail.strategies[sellStrategy] || detail.strategies['tax_efficient'];
      } else {
        raiseCashData = rawRaiseCash;
      }
    }

    // Check if sell-strategy toggle should be shown
    const noMarginRaw = isMixed ? detail.raise_cash_result : (detail.raise_cash_result || detail);
    const marginRaw = isMixed ? detail.raise_cash_margin_result : (detail.raise_cash_margin_result || detail.raise_cash_result || detail);
    const activeRaw = marginMode === 'with_margin' ? marginRaw : noMarginRaw;
    const hasStrategies = (tradeType === 'sell' || isMixed)
      && (activeRaw?.strategies || detail?.strategies);

    // Resolve invest cash data (respects margin mode)
    const investCashData = isMixed
      ? (marginMode === 'with_margin'
        ? (detail.invest_cash_margin_result || detail.invest_cash_result)
        : detail.invest_cash_result)
      : (tradeType === 'buy'
        ? (marginMode === 'with_margin'
          ? (detail.invest_cash_margin_result || detail.invest_cash_result || detail)
          : (detail.invest_cash_result || detail))
        : null);

    // Get account allocations and trades based on category
    const accountAllocations = raiseCashData?.account_allocations || investCashData?.account_allocations || [];
    const sellTrades = raiseCashData?.trades || [];
    const buyTrades = investCashData?.trades || [];
    const allTrades = [...sellTrades, ...buyTrades];
    // Use live trades from SuggestedTradesTable edits if available, otherwise fall back to suggested
    const liveTrades = liveTradesOverrides[groupName] != null ? liveTradesOverrides[groupName] : allTrades;

    // Get allocation data for visualizations
    const securityAllocations = raiseCashData?.security_allocations || investCashData?.security_allocations || [];

    // Apply any overrides coming from SuggestedTradesTable edits (computed after/weights)
    const overridden = allocationOverrides[groupName] || {};
    // Only use overrides if they are non-empty arrays, otherwise fallback to computed allocations
    const useSecurityAllocations = Array.isArray(overridden.security) && overridden.security.length > 0 ? overridden.security : securityAllocations;

    function aggregateSleevesFromSecurities(securities) {
      const sleeveMap = {};
      let hasAnySleeve = false;
      // helper to read multiple possible key naming styles (camelCase, snake_case, spaced)
      const getField = (obj, camel, snake, spaced) => {
        if (!obj) return 0;
        if (obj[camel] !== undefined && obj[camel] !== null) return obj[camel];
        if (obj[snake] !== undefined && obj[snake] !== null) return obj[snake];
        if (obj[spaced] !== undefined && obj[spaced] !== null) return obj[spaced];
        return 0;
      };

      securities.forEach(sec => {
        const secName = sec.name || sec['Security Level Model Name'] || '';
        const targetWeight = getField(sec, 'targetWeight', 'target_weight', 'Target Weight') || 0;

        // Skip RESTRICTED items - they're shown in Portfolio Summary, not allocation bars
        if (secName.startsWith('RESTRICTED ')) {
          return;
        }

        // Special handling: Keep "Excess Cash" as its own entry (not grouped into a sleeve)
        if (secName === 'Excess Cash') {
          hasAnySleeve = true;
          sleeveMap[secName] = {
            name: secName,
            beforeWeight: getField(sec, 'beforeWeight', 'before_weight', 'Actual Weight') || sec.beforeWeight || 0,
            afterWeight: getField(sec, 'afterWeight', 'after_weight', 'After Weight') || sec.afterWeight || 0,
            targetWeight: targetWeight,
            beforeValue: getField(sec, 'beforeValue', 'before_value', 'Holdings Current Value') || 0,
            afterValue: getField(sec, 'afterValue', 'after_value', 'After Value') || 0,
            targetValue: getField(sec, 'targetValue', 'target_value', 'Target Value') || 0,
            tradeImpact: getField(sec, 'tradeImpact', 'trade_impact', 'tradeImpact') || 0
          };
          return;
        }

        // Get sleeve name - use the security's sleeve property, fall back to Sub Model Name or spaced keys
        const rawSleeveName = (
          sec.sleeveName ||
          sec.sleeve_name ||
          sec['Sleeve Name'] ||
          sec['sleeve name'] ||
          sec.subModelName ||
          sec.sub_model_name ||
          sec['Sub Model Name'] ||
          sec['sub model name'] ||
          ''
        );
        let sleeveName;

        // Cash entries (UNASSIGNED CASH label, or raw cash/money-market symbols)
        // should always map to "Excess Cash", not "Other Holdings"
        const cashSymbols = rebalancerConstants?.cash_like_symbols || [];
        const isCashEntry = (
          secName === 'Excess Cash' ||
          secName === 'UNASSIGNED CASH' ||
          secName === 'UNASSIGNED_CASH' ||
          cashSymbols.includes(secName)
        );

        if (rawSleeveName && rawSleeveName.trim() !== '') {
          // Has a sleeve - use it
          sleeveName = rawSleeveName;
        } else if (isCashEntry) {
          // Cash with no sleeve → Excess Cash bucket
          sleeveName = 'Excess Cash';
        } else if (targetWeight > 0) {
          // No sleeve but has a target - use security name as its own entry
          sleeveName = secName;
        } else {
          // No sleeve and no target - group under 'Other Holdings'
          sleeveName = 'Other Holdings';
        }

        if (!sleeveName) return;
        hasAnySleeve = true;
        if (!sleeveMap[sleeveName]) {
          sleeveMap[sleeveName] = {
            name: sleeveName,
            beforeWeight: 0,
            afterWeight: 0,
            targetWeight: 0,
            beforeValue: 0,
            afterValue: 0,
            targetValue: 0,
            tradeImpact: 0
          };
        }
        sleeveMap[sleeveName].beforeWeight += getField(sec, 'beforeWeight', 'before_weight', 'Actual Weight') || sec.beforeWeight || 0;
        sleeveMap[sleeveName].afterWeight += getField(sec, 'afterWeight', 'after_weight', 'After Weight') || sec.afterWeight || 0;
        sleeveMap[sleeveName].targetWeight += targetWeight || 0;
        sleeveMap[sleeveName].beforeValue += getField(sec, 'beforeValue', 'before_value', 'Holdings Current Value') || 0;
        sleeveMap[sleeveName].afterValue += getField(sec, 'afterValue', 'after_value', 'After Value') || 0;
        sleeveMap[sleeveName].targetValue += getField(sec, 'targetValue', 'target_value', 'Target Value') || 0;
        sleeveMap[sleeveName].tradeImpact += getField(sec, 'tradeImpact', 'trade_impact', 'tradeImpact') || 0;
      });
      // If no securities had a sleeve, return empty array (no sleeves for this group)
      return hasAnySleeve ? Object.values(sleeveMap) : [];
    }
    const useSleeveAllocations = aggregateSleevesFromSecurities(useSecurityAllocations);

    // Filter out RESTRICTED and non-model (Other Holdings) entries for the allocation bars and pie chart.
    // RESTRICTED items are shown in the Portfolio Summary pie already; Other Holdings are non-tradeable
    // assets (e.g. from margin accounts) that have no place in a trade-oriented view.
    // After filtering, renormalize weights so bars still sum to 100%.
    const normalizeAllocWeights = (items) => {
      const totBefore = items.reduce((s, i) => s + (i.beforeWeight || i.before_weight || 0), 0);
      const totAfter  = items.reduce((s, i) => s + (i.afterWeight  || i.after_weight  || 0), 0);
      const totTarget = items.reduce((s, i) => s + (i.targetWeight || i.target_weight || 0), 0);
      return items.map(item => ({
        ...item,
        beforeWeight: totBefore > 0 ? ((item.beforeWeight || item.before_weight || 0) / totBefore) * 100 : 0,
        afterWeight:  totAfter  > 0 ? ((item.afterWeight  || item.after_weight  || 0) / totAfter)  * 100 : 0,
        targetWeight: totTarget > 0 ? ((item.targetWeight || item.target_weight || 0) / totTarget) * 100 : 0,
      }));
    };
    // Sort allocations: model positions first, then Other Holdings, then Restricted, then Excess Cash.
    const sortAllocations = (items) => items.slice().sort((a, b) => {
      const rank = (name) => {
        if (name === 'Excess Cash') return 3;
        if (name?.startsWith('RESTRICTED')) return 2;
        if (name === 'Other Holdings') return 1;
        return 0;
      };
      return rank(a.name) - rank(b.name);
    });

    const filteredSecurityAllocations = sortAllocations(normalizeAllocWeights(
      useSecurityAllocations.filter(item =>
        !item.name?.startsWith('RESTRICTED ')
      )
    ));
    const filteredSleeveAllocations = sortAllocations(normalizeAllocWeights(
      useSleeveAllocations.filter(item =>
        !item.name?.startsWith('RESTRICTED ')
      )
    ));

    // Generate consistent colors for allocations
    const generateColorMap = (allocations) => {
      const colors = [
        '#0d6efd', '#198754', '#dc3545', '#ffc107', '#0dcaf0', '#6f42c1',
        '#fd7e14', '#20c997', '#d63384', '#6c757d', '#495057', '#adb5bd'
      ];
      const colorMap = {};
      allocations.forEach((item, index) => {
        const name = item.name || item.sleeveName || 'Unknown';
        colorMap[name] = colors[index % colors.length];
      });
      return colorMap;
    };

    const colorMap = generateColorMap([...useSleeveAllocations, ...useSecurityAllocations]);

    // ── noAction: full layout with portfolio pie, allocation bars, account cards, manual trade entry ──
    if (category === 'noAction') {
      const naAccountAllocations = detail?.account_allocations || [];
      // Build security allocations from live holdings — carry sleeve/model info through
      const holdingsArr = Object.values(allAccountHoldings).flat();
      const totalPortfolioVal = holdingsArr.reduce((sum, h) => sum + (h['Holdings Current Value'] || 0), 0);
      const naSecMap = {};
      holdingsArr.forEach(h => {
        const rawName = h['Security Level Model Name'] || h['Symbol'] || 'Unknown';
        // Normalize cash labels so they aggregate under "Excess Cash" in both Sleeve and Security views
        const cashSymbols = rebalancerConstants?.cash_like_symbols || [];
        const isCash = (
          rawName === 'UNASSIGNED CASH' ||
          rawName === 'UNASSIGNED_CASH' ||
          cashSymbols.includes(rawName)
        );
        const name = isCash ? 'Excess Cash' : rawName;
        const sleeve = isCash ? '' : (h['Sleeve Name'] || '');
        const subModel = h['Sub Model Name'] || '';
        const val = h['Holdings Current Value'] || 0;
        const tw = isCash ? 0 : (h['Target Weight'] || 0);
        const key = `${sleeve}||${name}`;
        if (!naSecMap[key]) {
          naSecMap[key] = {
            name,
            sleeveName: sleeve,
            subModelName: subModel,
            beforeValue: 0, afterValue: 0,
            beforeWeight: 0, afterWeight: 0,
            targetWeight: 0, tradeImpact: 0
          };
        }
        naSecMap[key].beforeValue += val;
        naSecMap[key].afterValue += val;
        // Target weight is a model property, not a value to sum — take max to preserve it
        if (tw > naSecMap[key].targetWeight) naSecMap[key].targetWeight = tw;
      });
      const naSecAllocations = Object.values(naSecMap).map(s => ({
        ...s,
        beforeWeight: totalPortfolioVal > 0 ? (s.beforeValue / totalPortfolioVal) * 100 : 0,
        afterWeight: totalPortfolioVal > 0 ? (s.afterValue / totalPortfolioVal) * 100 : 0,
      }));
      const naSleeveAllocations = aggregateSleevesFromSecurities(naSecAllocations);
      const naFilteredSec = sortAllocations(naSecAllocations.filter(s => !s.name?.startsWith('RESTRICTED ')));
      const naFilteredSleeve = sortAllocations(naSleeveAllocations.filter(s => !s.name?.startsWith('RESTRICTED ')));
      const naColorMap = generateColorMap([...naSleeveAllocations, ...naSecAllocations]);
      const naOverridden = allocationOverrides[groupName] || {};
      const naUseSec = Array.isArray(naOverridden.security) && naOverridden.security.length > 0 ? naOverridden.security : naSecAllocations;

      return (
        <div className="p-3 bg-light">
          {/* Summary Row */}
          <div className="row mb-3">
            <div className="col-md-3">
              <div className="card h-100">
                <div className="card-body py-2">
                  <small className="text-muted d-block">Status</small>
                  <strong className="text-secondary">No Action Required</strong>
                  <small className="text-muted d-block mt-1">All accounts within cash reserve thresholds</small>
                  {naAccountAllocations.some(a => (a.marginCashUsed || 0) > 0) && (
                    <div className="mt-2">
                      <span className="badge bg-warning text-dark">
                        <i className="bi bi-exclamation-triangle me-1"></i>Margin Accounts
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <CashSummaryCard
                accountAllocations={naAccountAllocations}
                allAccountHoldings={allAccountHoldings}
                trades={[]}
                cashLikeSymbols={rebalancerConstants?.cash_like_symbols}
                isLoading={isLoadingHoldings}
                ignoreMargin={ignoreMargin}
              />
            </div>
            <div className="col-md-6">
              <PortfolioSummaryPieChart
                securityAllocations={naUseSec}
                accountAllocations={naAccountAllocations}
                trades={[]}
                isLoading={isLoadingHoldings}
                ignoreMargin={ignoreMargin}
              />
            </div>
          </div>

          {/* Allocation Bars */}
          {(naFilteredSleeve.length > 0 || naFilteredSec.length > 0) && (
            <AllocationVisualization
              sleeveAllocations={naFilteredSleeve}
              securityAllocations={naFilteredSec}
              colorMap={naColorMap}
              viewType={allocViewType}
              onViewTypeChange={setAllocViewType}
            />
          )}

          {/* Account Cards */}
          {naAccountAllocations.length > 0 && (
            <div className="mb-3">
              <h6 className="mb-2">
                <i className="bi bi-wallet2 me-2"></i>
                Accounts ({naAccountAllocations.length})
              </h6>
              <div className="d-flex flex-wrap gap-2">
                {naAccountAllocations.map((account, idx) => {
                  const cashCurrent = account.cashCurrent || 0;
                  const marginUsed = account.marginCashUsed || 0;
                  // When ignoring margin, show only the non-margin portion of cash
                  const displayCash = ignoreMargin ? cashCurrent + marginUsed : cashCurrent;
                  const cashMin = account.cashReserveMin || 0;
                  const cashCenter = account.cashReserveCenter || 0;
                  const cashMax = account.cashReserveMax || 0;
                  const hasReserveTarget = cashCenter > 0 || cashMin > 0 || cashMax > 0;
                  let cashStatus = 'In Range';
                  let statusClass = 'bg-success';
                  if (!hasReserveTarget) { cashStatus = null; statusClass = null; }
                  else if (displayCash < cashMin) { cashStatus = 'Below Min'; statusClass = 'bg-danger'; }
                  else if (displayCash > cashMax) { cashStatus = 'Above Max'; statusClass = 'bg-warning text-dark'; }
                  const numAccounts = naAccountAllocations.length;
                  const flexBasis = numAccounts <= 2 ? '48%' : numAccounts <= 3 ? '32%' : numAccounts <= 4 ? '24%' : numAccounts <= 6 ? '16%' : '15%';
                  return (
                    <div key={idx} className="card" style={{ flex: `1 1 ${flexBasis}`, minWidth: '150px', borderLeft: `4px solid ${account.accountType === 'taxable' ? '#ffc107' : account.accountType === 'tax_deferred' ? '#0dcaf0' : '#198754'}` }}>
                      <div className="card-body py-2 px-2">
                        <div className="text-center border-bottom pb-1 mb-1">
                          <button className="btn btn-link p-0 fw-bold text-truncate d-block w-100" onClick={(e) => { e.stopPropagation(); openCashReserveDetails(account.accountNumber, account.accountName); }} title="View cash reserve details" style={{ fontSize: '0.85rem' }}>{account.accountName}</button>
                          <small className="text-muted" style={{ fontSize: '0.7rem' }}>{account.accountNumber}</small>
                        </div>
                        <div className="text-center mb-1" style={{ fontSize: '0.8rem' }}>
                          <div><span className="text-muted">Cash: </span><strong style={{ color: displayCash < 0 ? '#dc3545' : undefined }}>{formatCurrency(displayCash)}</strong></div>
                          <div><span className="text-muted">Target: </span><strong>{formatCurrency(cashCenter)}</strong></div>
                        </div>
                        <div className="text-center mb-1" style={{ fontSize: '0.7rem' }}>
                          <div className="text-muted">{formatCurrency(cashMin)} — {formatCurrency(cashCenter)} — {formatCurrency(cashMax)}</div>
                        </div>
                        <div className="text-center">
                          {cashStatus && (
                            <span className={`badge ${statusClass}`} style={{ fontSize: '0.65rem' }}>{cashStatus}</span>
                          )}
                          {(account.marginCashUsed || 0) > 0 && (
                            <span className="badge bg-warning text-dark ms-1" style={{ fontSize: '0.65rem' }}>Margin</span>
                          )}
                        </div>
                        <AccountAllocationBars
                          holdings={allAccountHoldings[account.accountNumber] || []}
                          trades={[]}
                          colorMap={naColorMap}
                          viewType={allocViewType}
                          cashLikeSymbols={rebalancerConstants?.cash_like_symbols}
                          cashReserveMax={cashMax}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Suggested Trades — empty by default, lets user add manual trades */}
          <SuggestedTradesTable
            trades={[]}
            accountAllocations={naAccountAllocations}
            securityAllocations={naUseSec}
            allAccountHoldings={allAccountHoldings}
            onQueueTrades={handleQueueTrades}
            isQueuingTrades={isQueuingTrades}
            queuedTradesData={showQueuedTrades ? queuedTrades[groupName] : null}
            showQueuedTrades={showQueuedTrades}
            onToggleQueuedView={setShowQueuedTrades}
            onQueueTriggerReady={(fn) => { queueTradesTriggerRef.current = fn; }}
            onAllocationsChange={(payload) => {
              setAllocationOverrides(prev => ({ ...prev, [groupName]: { security: payload.security } }));
            }}
            onTradesChange={(lt) => {
              setLiveTradesOverrides(prev => ({ ...prev, [groupName]: lt }));
            }}
          />
        </div>
      );
    }

    return (
      <div className="p-3 bg-light">
        {/* Summary Stats Row */}
        <div className="row mb-3">
          {isMixed ? (
            <>
              <div className="col-md-3">
                <div className="card h-100 border-danger">
                  <div className="card-body py-2">
                    <small className="text-muted d-block">Sell Trades</small>
                    <strong className="text-danger">{raiseCashData?.total_trades ?? 0}</strong>
                    <small className="text-muted d-block">{formatCurrency(raiseCashData?.total_trade_value)}</small>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card h-100 border-success">
                  <div className="card-body py-2">
                    <small className="text-muted d-block">Buy Trades</small>
                    <strong className="text-success">{investCashData?.total_trades ?? 0}</strong>
                    <small className="text-muted d-block">{formatCurrency(investCashData?.total_trade_value)}</small>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card h-100">
                  <div className="card-body py-2">
                    <small className="text-muted d-block">Accounts to Raise</small>
                    <strong>{detail.analysis?.accounts_need_raise?.length ?? 0}</strong>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card h-100">
                  <div className="card-body py-2">
                    <small className="text-muted d-block">Accounts to Invest</small>
                    <strong>{detail.analysis?.accounts_need_invest?.length ?? 0}</strong>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Left side: Trade Summary Card */}
              <div className="col-md-3">
                <div className="card h-100">
                  <div className="card-header bg-light py-2 px-3">
                    <h6 className="mb-0">
                      <i className="bi bi-clipboard-data me-2"></i>
                      Trade Summary
                    </h6>
                  </div>
                  <div className="card-body py-2 px-3">
                    <div className="d-flex flex-column gap-2">
                      {/* Model Name(s) */}
                      {(() => {
                        const uniqueModels = [...new Set(accountAllocations.map(a => a.modelName).filter(Boolean))];
                        const hasMultipleModels = uniqueModels.length > 1;
                        return uniqueModels.length > 0 && (
                          <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                            <small className="text-muted">
                              <i className="bi bi-diagram-3 me-1"></i>
                              Model{hasMultipleModels ? 's' : ''}
                            </small>
                            <span className={hasMultipleModels ? 'text-warning fw-bold' : 'fw-bold'}>
                              {hasMultipleModels && <i className="bi bi-exclamation-triangle me-1" title="Multiple models in this group"></i>}
                              {uniqueModels.join(', ')}
                            </span>
                          </div>
                        );
                      })()}

                      {/* Stats - Vertical Layout */}
                      <div className="d-flex flex-column gap-1">
                        <div className="d-flex justify-content-between align-items-center">
                          <small className="text-muted">Total Trades</small>
                          <strong>{raiseCashData?.total_trades ?? investCashData?.total_trades ?? 0}</strong>
                        </div>
                        <div className="d-flex justify-content-between align-items-center">
                          <small className="text-muted">Trade Value</small>
                          <strong>{formatCurrency(raiseCashData?.total_trade_value ?? investCashData?.total_trade_value)}</strong>
                        </div>
                        <div className="d-flex justify-content-between align-items-center">
                          <small className="text-muted">Cash Needs Met</small>
                          <strong>{formatPercent(raiseCashData?.target_achievement ?? investCashData?.target_achievement)}</strong>
                        </div>
                        <div className="d-flex justify-content-between align-items-center">
                          <small className="text-muted">
                            {tradeType === 'sell' ? 'Cash Violations' : 'Cash Excess'}
                          </small>
                          <strong className={(raiseCashData?.cash_violations?.length ?? investCashData?.cash_violations?.length) > 0 ? 'text-warning' : 'text-success'}>
                            {raiseCashData?.cash_violations?.length ?? investCashData?.cash_violations?.length ?? 0}
                          </strong>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Middle: Cash Summary Card */}
              <div className="col-md-3">
                <CashSummaryCard
                  accountAllocations={accountAllocations}
                  allAccountHoldings={allAccountHoldings}
                  trades={liveTrades}
                  cashLikeSymbols={rebalancerConstants?.cash_like_symbols}
                  isLoading={isLoadingHoldings}
                  ignoreMargin={ignoreMargin}
                />
              </div>
              {/* Right side: Portfolio Summary Pie Chart */}
              <div className="col-md-6">
                <PortfolioSummaryPieChart securityAllocations={filteredSecurityAllocations} accountAllocations={accountAllocations} trades={liveTrades} isLoading={isLoadingHoldings || filteredSecurityAllocations.length === 0} ignoreMargin={ignoreMargin} />
              </div>
            </>
          )}
        </div>

        {/* Allocation Visualization - Toggleable Sleeve/Security View */}
        {(filteredSleeveAllocations.length > 0 || filteredSecurityAllocations.length > 0) && (
          <AllocationVisualization
            sleeveAllocations={filteredSleeveAllocations}
            securityAllocations={filteredSecurityAllocations}
            colorMap={colorMap}
            viewType={allocViewType}
            onViewTypeChange={setAllocViewType}
          />
        )}

        {/* Account Cards - Horizontal Layout */}
        {accountAllocations.length > 0 && (
          <div className="mb-3">
            <h6 className="mb-2">
              <i className="bi bi-wallet2 me-2"></i>
              Accounts ({accountAllocations.length})
            </h6>
            <div className="d-flex flex-wrap gap-2">
              {accountAllocations.map((account, idx) => {
                // Calculate trades value for this account — use live edited trades.
                // Use action-based sign normalization so both backend unsigned trade_value
                // AND collectAllTrades() signed trade_value work correctly:
                // sells increase cash (+), buys decrease cash (-)
                // Support both lowercase (SuggestedTradesTable) and PascalCase (backend) field names
                const accountTrades = liveTrades.filter(t =>
                  (t.account_number || t.AccountNumber || t['Account Number'] || '') === account.accountNumber
                );
                const tradeCashImpact = accountTrades.reduce((sum, t) => {
                  const absVal = Math.abs(t.trade_value || t.value || t.TradeAmount || t.trade_amount || 0);
                  const action = t.action || t.Action || '';
                  const isBuy = action === 'BUY' || (!action && (t.quantity || 0) > 0);
                  return sum + (isBuy ? -absVal : absVal);
                }, 0);

                // Cash reserve status
                const rawCash = account.cashCurrent || 0;
                const marginUsed = account.marginCashUsed || 0;
                // Apply same double-subtraction correction as CashSummaryCard:
                // stale data may have cashCurrent = net_cash − margin, so undo it.
                const cashCurrent = (marginUsed > 0 && rawCash < -marginUsed)
                  ? rawCash + marginUsed
                  : rawCash;
                // When ignoring margin, show gross cash (add back margin debt)
                const displayCashCurrent = ignoreMargin ? cashCurrent + marginUsed : cashCurrent;
                const cashMin = account.cashReserveMin || 0;
                const cashCenter = account.cashReserveCenter || 0;
                const cashMax = account.cashReserveMax || 0;

                // cashAfterTrades: add the unified tradeCashImpact (sells positive, buys negative)
                const cashAfterTrades = displayCashCurrent + tradeCashImpact;

                const hasReserveTarget = cashCenter > 0 || cashMin > 0 || cashMax > 0;

                // Determine status
                let cashStatus = 'In Range';
                let statusClass = 'bg-success';
                if (!hasReserveTarget) { cashStatus = null; statusClass = null; }
                else if (cashAfterTrades < cashMin) {
                  cashStatus = 'Below Min';
                  statusClass = 'bg-danger';
                } else if (cashAfterTrades > cashMax) {
                  cashStatus = 'Above Max';
                  statusClass = 'bg-warning text-dark';
                }

                // Calculate flex basis based on number of accounts
                const numAccounts = accountAllocations.length;
                const flexBasis = numAccounts <= 2 ? '48%' :
                  numAccounts <= 3 ? '32%' :
                    numAccounts <= 4 ? '24%' :
                      numAccounts <= 6 ? '16%' : '15%';

                return (
                  <div
                    key={idx}
                    className="card"
                    style={{
                      flex: `1 1 ${flexBasis}`,
                      minWidth: '150px',
                      borderLeft: `4px solid ${account.accountType === 'taxable' ? '#ffc107' :
                        account.accountType === 'tax_deferred' ? '#0dcaf0' :
                          '#198754'
                        }`
                    }}
                  >
                    <div className="card-body py-2 px-2">
                      <div className="text-center border-bottom pb-1 mb-1">
                        <button
                          className="btn btn-link p-0 fw-bold text-truncate d-block w-100"
                          onClick={(e) => { e.stopPropagation(); openCashReserveDetails(account.accountNumber, account.accountName); }}
                          title="View cash reserve details"
                          style={{ fontSize: '0.85rem' }}
                        >
                          {account.accountName}
                        </button>
                        <small className="text-muted" style={{ fontSize: '0.7rem' }}>{account.accountNumber}</small>
                      </div>

                      <div className="text-center mb-1" style={{ fontSize: '0.8rem' }}>
                        <div>
                          <span className="text-muted">Cash: </span>
                          <strong style={{ color: displayCashCurrent < 0 ? '#dc3545' : undefined }}>{formatCurrency(displayCashCurrent)}</strong>
                        </div>
                        <div>
                          <span className="text-muted">After: </span>
                          <strong className={cashStatus === 'In Range' ? 'text-success' : cashStatus === 'Below Min' ? 'text-danger' : 'text-warning'}>
                            {formatCurrency(cashAfterTrades)}
                          </strong>
                        </div>
                      </div>

                      <div className="text-center mb-1" style={{ fontSize: '0.7rem' }}>
                        <div className="text-muted">
                          {formatCurrency(cashMin)} - {formatCurrency(cashCenter)} - {formatCurrency(cashMax)}
                        </div>
                      </div>

                      <div className="text-center">
                        {cashStatus && (
                          <span className={`badge ${statusClass}`} style={{ fontSize: '0.65rem' }}>
                            {cashStatus}
                          </span>
                        )}
                        {accountTrades.length > 0 && (
                          <span className="badge bg-primary ms-1" style={{ fontSize: '0.65rem' }}>
                            {accountTrades.length} trade{accountTrades.length !== 1 ? 's' : ''}
                          </span>
                        )}
                        {(account.marginCashUsed || 0) > 0 && (
                          <span className="badge bg-warning text-dark ms-1" style={{ fontSize: '0.65rem' }}>Margin</span>
                        )}
                      </div>
                      <AccountAllocationBars
                        holdings={allAccountHoldings[account.accountNumber] || []}
                        trades={accountTrades}
                        colorMap={colorMap}
                        viewType={allocViewType}
                        cashLikeSymbols={rebalancerConstants?.cash_like_symbols}
                        cashReserveMax={cashMax}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Cash Violations (for raise cash) */}
        {(raiseCashData?.cash_violations?.length > 0) && (
          <div className="mb-3">
            <h6 className="mb-2">
              <i className="bi bi-exclamation-triangle text-warning me-2"></i>
              Cash Violations
            </h6>
            <ul className="list-group">
              {raiseCashData.cash_violations.map((violation, idx) => (
                <li key={idx} className="list-group-item list-group-item-warning py-2">
                  <small>{violation}</small>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Isaac manual review flag */}
        {sellStrategy === 'isaac' && raiseCashData?.flagged_for_review && (
          <div className="alert alert-danger mb-3">
            <h6 className="alert-heading mb-1">
              <i className="bi bi-slash-circle me-2"></i>
              Flagged for Manual Review — ALL Trades Suppressed
            </h6>
            <p className="mb-1" style={{ fontSize: '0.875rem' }}>
              Insufficient cash could not be raised even after reducing sleeve and security
              midpoints to&nbsp;80&nbsp;% of their targets. No trades have been generated for
              this household. Resolve the sell restrictions below before re-running.
            </p>
            {raiseCashData?.restriction_report?.length > 0 && (
              <ul className="mb-0 mt-2" style={{ fontSize: '0.8rem' }}>
                {raiseCashData.restriction_report.map((line, i) => (
                  <li key={i}><code>{line}</code></li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Rebalancing Summary */}
        {(investCashData?.trades?.length > 0 || raiseCashData?.trades?.length > 0) && (
          <div className="mb-3">
            <div className="card">
              <div
                className="card-header bg-primary text-white py-2 px-3 d-flex justify-content-between align-items-center"
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  const summaryBody = document.getElementById(`summary-${groupName}`);
                  if (summaryBody) {
                    summaryBody.style.display = summaryBody.style.display === 'none' ? 'block' : 'none';
                  }
                }}
              >
                <h6 className="mb-0">
                  <i className="bi bi-file-text me-2"></i>
                  Rebalancing Summary & Explanation
                </h6>
                <i className="bi bi-chevron-down"></i>
              </div>
              <div id={`summary-${groupName}`} style={{ display: 'none' }}>
                <div className="card-body py-2 px-3">
                  <RebalancingSummaryCards
                    rebalanceResult={investCashData || raiseCashData}
                    hideDollarValues={false}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Restricted-buys notice: all underweight positions blocked by restrictions */}
        {buyTrades.length === 0 && investCashData?.restriction_violations?.length > 0 && (
          <RestrictedBuysNotice
            restrictions={investCashData.restriction_violations}
            groupName={groupName}
            onRerunComplete={(result) => applyBuyToMaxResult(groupName, category, result)}
          />
        )}

        {/* Suggested Trades */}
        <div>
          <SuggestedTradesTable
            trades={allTrades}
            formatCurrency={formatCurrency}
            accountAllocations={accountAllocations}
            securityAllocations={securityAllocations}
            allAccountHoldings={allAccountHoldings}
            onQueueTrades={handleQueueTrades}
            isQueuingTrades={isQueuingTrades}
            queuedTradesData={queuedTrades[selectedGroupForDetail?.name]}
            showQueuedTrades={showQueuedTrades}
            onToggleQueuedView={setShowQueuedTrades}
            onQueueTriggerReady={(fn) => { queueTradesTriggerRef.current = fn; }}
            onAllocationsChange={(payload) => {
              // payload: { security: [...] }
              // Only save security overrides; sleeves are always derived from security allocations
              setAllocationOverrides(prev => ({
                ...prev,
                [groupName]: { security: payload.security }
              }));
            }}
            onTradesChange={(liveTrades) => {
              setLiveTradesOverrides(prev => ({ ...prev, [groupName]: liveTrades }));
            }}
          />
        </div>
      </div>
    );
  }, [clientDetails, loadingClients, selectedGroupForDetail, sellStrategy, allAccountHoldings, handleQueueTrades, isQueuingTrades, queuedTrades, showQueuedTrades, allocationOverrides, liveTradesOverrides, marginMode, ignoreMargin, rebalancerConstants, allocViewType, setAllocViewType, applyBuyToMaxResult]);

  // Generate row ID for rows
  const generateRowId = useCallback((row) => row.name, []);

  // Get the most recent timestamp from all summaries
  const getLatestTimestamp = () => {
    const timestamps = [
      raiseCashSummary?.timestamp,
      investCashSummary?.timestamp,
      mixedSummary?.timestamp
    ].filter(Boolean);

    if (timestamps.length === 0) return null;
    return timestamps.sort().reverse()[0];
  };

  // Calculate combined stats — memoized so RebalancerHistoryCharts doesn't re-render on unrelated state changes
  const stats = useMemo(() => {
    const raiseCashGroups = raiseCashSummary?.total_groups || 0;
    const investCashGroups = investCashSummary?.total_groups || 0;
    const mixedGroups = mixedSummary?.total_groups || 0;

    const raiseCashTrades = raiseCashSummary?.total_trades || 0;
    const investCashTrades = investCashSummary?.total_trades || 0;

    const raiseCashValue = raiseCashSummary?.total_trade_value || 0;
    const investCashValue = investCashSummary?.total_trade_value || 0;

    return {
      totalGroups: raiseCashGroups + investCashGroups + mixedGroups,
      raiseCashGroups,
      investCashGroups,
      mixedGroups,
      raiseCashTrades,
      investCashTrades,
      raiseCashValue,
      investCashValue
    };
  }, [raiseCashSummary, investCashSummary, mixedSummary]);
  const latestTimestamp = getLatestTimestamp();
  const hasAnyData = raiseCashSummary || investCashSummary || mixedSummary;

  // Count total queued trades
  const totalQueuedTrades = queuedTradesSummary?.total_trades || 0;
  const hasQueuedTrades = totalQueuedTrades > 0;

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">
            <i className="bi bi-speedometer2 me-2"></i>
            Rebalancer Dashboard
          </h2>
          <p className="text-muted mb-0">Daily Cash Management Summary</p>
        </div>
        <div className="text-end d-flex flex-column align-items-end gap-2">
          <div className="d-flex gap-2">
            {/* Select All queued groups */}
            {hasQueuedTrades && (
              <button
                className="btn btn-outline-secondary"
                onClick={() => {
                  const allGroups = Object.keys(queuedTrades || {});
                  if (!allGroups || allGroups.length === 0) return;
                  if (selectedQueuedGroups && selectedQueuedGroups.size === allGroups.length) {
                    setSelectedQueuedGroups(new Set());
                  } else {
                    setSelectedQueuedGroups(new Set(allGroups));
                  }
                }}
                disabled={isDownloadingOrders}
                title="Select or clear all queued groups"
              >
                <i className="bi bi-check2-square me-2"></i>
                Select All
              </button>
            )}
            {/* Download Order Files Button */}
            {hasQueuedTrades && (
              <button
                className="btn btn-success"
                onClick={selectedQueuedGroups && selectedQueuedGroups.size > 0 ? handleDownloadSelectedQueuedOrders : handleDownloadBulkOrders}
                disabled={isDownloadingOrders}
                title={selectedQueuedGroups && selectedQueuedGroups.size > 0 ? `Download ${selectedQueuedGroups.size} selected group(s)` : `Download order files for ${totalQueuedTrades} queued trades (${queuedTradesSummary?.by_custodian?.Schwab || 0} Schwab, ${queuedTradesSummary?.by_custodian?.Fidelity || 0} Fidelity)`}
              >
                <i className={`bi ${isDownloadingOrders ? 'bi-arrow-repeat spin' : (selectedQueuedGroups && selectedQueuedGroups.size > 0 ? 'bi-download' : 'bi-download')} me-2`}></i>
                {selectedQueuedGroups && selectedQueuedGroups.size > 0 ? `Download Selected (${selectedQueuedGroups.size})` : 'Download Orders'}
                <span className="badge bg-light text-success ms-2">{selectedQueuedGroups && selectedQueuedGroups.size > 0 ? selectedQueuedGroups.size : totalQueuedTrades}</span>
              </button>
            )}

            {/* Refresh Prices Button */}
            <RefreshPricesButton
              mode="all"
              size="md"
              onRefreshComplete={() => {
                // Background job saved results to S3. Invalidate
                // all cached client details so they reload fresh.
                setClientDetails({ raiseCash: {}, investCash: {}, mixed: {} });
                fetchAllSummaries();
              }}
            />
          </div>

          {/* Queued Trades Summary */}
          {hasQueuedTrades && (
            <div className="text-muted small">
              <i className="bi bi-clock-fill text-success me-1"></i>
              {selectedQueuedGroups && selectedQueuedGroups.size > 0 ? (
                <span>
                  <strong>{selectedQueuedGroups.size}</strong> selected of <strong>{queuedTradesSummary?.groups_count || 0}</strong> groups queued:
                </span>
              ) : (
                <span>
                  <strong>{queuedTradesSummary?.groups_count || 0}</strong> groups queued:
                </span>
              )}
              {queuedTradesSummary?.by_custodian?.Schwab > 0 && (
                <span className="ms-1">
                  <span className="badge bg-info">{queuedTradesSummary.by_custodian.Schwab} Schwab</span>
                </span>
              )}
              {queuedTradesSummary?.by_custodian?.Fidelity > 0 && (
                <span className="ms-1">
                  <span className="badge bg-primary">{queuedTradesSummary.by_custodian.Fidelity} Fidelity</span>
                </span>
              )}
              {queuedTradesSummary?.by_custodian?.Other > 0 && (
                <span className="ms-1">
                  <span className="badge bg-secondary">{queuedTradesSummary.by_custodian.Other} Other</span>
                </span>
              )}
            </div>
          )}

          {!isLoading && latestTimestamp && (
            <div className="text-muted small">
              <i className="bi bi-clock me-2"></i>
              Last Run: <strong>{formatTimestamp(latestTimestamp)}</strong>
            </div>
          )}
        </div>
      </div>

      {/* History Trend Charts with Stats Widgets */}
      <RebalancerHistoryCharts days={30} stats={hasAnyData ? stats : null} />


      {/* Loading */}
      {isLoading && (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {/* No data message */}
      {!isLoading && !hasAnyData && (
        <div className="alert alert-info">
          <i className="bi bi-info-circle me-2"></i>
          No daily rebalancer data found. The daily rebalancer may not have been run yet.
        </div>
      )}

      {/* Category Tabs */}
      {!isLoading && (
        <>
          <div className="d-flex justify-content-between align-items-end mb-0">
            <ul className="nav nav-tabs mb-0">
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === 'raiseCash' ? 'active' : ''}`}
                  onClick={() => setActiveTab('raiseCash')}
                  style={{
                    backgroundColor: activeTab === 'raiseCash' ? '#dc3545' : 'transparent',
                    color: activeTab === 'raiseCash' ? 'white' : '#dc3545',
                    borderColor: '#dc3545',
                    borderBottomColor: activeTab === 'raiseCash' ? '#dc3545' : undefined,
                    fontWeight: 'bold',
                    borderRadius: activeTab === 'raiseCash' ? '0.375rem 0.375rem 0 0' : undefined
                  }}
                >
                  <i className="bi bi-arrow-up-circle me-2"></i>
                  Raise Cash ({raiseCashSummary?.groups?.filter(g => g.success).length || 0})
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === 'investCash' ? 'active' : ''}`}
                  onClick={() => setActiveTab('investCash')}
                  style={{
                    backgroundColor: activeTab === 'investCash' ? '#198754' : 'transparent',
                    color: activeTab === 'investCash' ? 'white' : '#198754',
                    borderColor: '#198754',
                    borderBottomColor: activeTab === 'investCash' ? '#198754' : undefined,
                    fontWeight: 'bold',
                    borderRadius: activeTab === 'investCash' ? '0.375rem 0.375rem 0 0' : undefined
                  }}
                >
                  <i className="bi bi-arrow-down-circle me-2"></i>
                  Invest Cash ({investCashSummary?.groups?.filter(g => g.success).length || 0})
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === 'mixed' ? 'active' : ''}`}
                  onClick={() => setActiveTab('mixed')}
                  style={{
                    backgroundColor: activeTab === 'mixed' ? '#ffc107' : 'transparent',
                    color: activeTab === 'mixed' ? '#212529' : '#997404',
                    borderColor: '#ffc107',
                    borderBottomColor: activeTab === 'mixed' ? '#ffc107' : undefined,
                    fontWeight: 'bold',
                    borderRadius: activeTab === 'mixed' ? '0.375rem 0.375rem 0 0' : undefined
                  }}
                >
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  Mixed ({mixedSummary?.groups?.length || 0})
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === 'noAction' ? 'active' : ''}`}
                  onClick={() => setActiveTab('noAction')}
                  style={{
                    backgroundColor: activeTab === 'noAction' ? '#6c757d' : 'transparent',
                    color: activeTab === 'noAction' ? 'white' : '#6c757d',
                    borderColor: '#6c757d',
                    borderBottomColor: activeTab === 'noAction' ? '#6c757d' : undefined,
                    fontWeight: 'bold',
                    borderRadius: activeTab === 'noAction' ? '0.375rem 0.375rem 0 0' : undefined
                  }}
                >
                  <i className="bi bi-check-circle me-2"></i>
                  No Action ({noActionSummary?.groups?.length || 0})
                </button>
              </li>
            </ul>
            <div className="input-group" style={{ width: '250px', marginBottom: '1px' }}>
              <span className="input-group-text bg-white border-end-0">
                <i className="bi bi-search text-muted"></i>
              </span>
              <input
                type="text"
                className="form-control border-start-0"
                placeholder="Search account family..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() => setSearchTerm('')}
                >
                  <i className="bi bi-x"></i>
                </button>
              )}
            </div>
          </div>

          {/* Tab Content */}
          <div className="tab-content">
            {/* RAISE CASH TAB */}
            {activeTab === 'raiseCash' && raiseCashSummary?.groups && (
              <div className="card border-danger border-top-0 rounded-top-0">
                <div className="card-header bg-danger text-white rounded-top-0">
                  <small className="opacity-75">Sell to increase cash reserves</small>
                </div>
                <div className="card-body p-0">
                  <div className="scrollable-table-container">
                    <SortableResizableTable
                      columns={summaryColumns}
                      hiddenPredicate={(item) => hiddenGroups.has(item.name)}
                      data={[...raiseCashSummary.groups]
                        .filter(g => g.success)
                        .filter(g => !searchTerm || g.name?.toLowerCase().includes(searchTerm.toLowerCase()))
                        .sort((a, b) => (b.value || 0) - (a.value || 0))}
                      onRowClick={(row) => openDetailModal(row, 'raiseCash')}
                      generateRowId={generateRowId}
                      emptyMessage="No Account Families need to raise cash"
                    />
                  </div>
                </div>
                {errors.raiseCash && (
                  <div className="card-footer bg-danger-subtle text-danger">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {errors.raiseCash}
                  </div>
                )}
              </div>
            )}

            {/* INVEST CASH TAB */}
            {activeTab === 'investCash' && investCashSummary?.groups && (
              <div className="card border-success border-top-0 rounded-top-0">
                <div className="card-header bg-success text-white rounded-top-0">
                  <small className="opacity-75">Buy to invest excess cash</small>
                </div>
                <div className="card-body p-0">
                  <div className="scrollable-table-container">
                    <SortableResizableTable
                      columns={summaryColumns}
                      hiddenPredicate={(item) => hiddenGroups.has(item.name)}
                      data={[...investCashSummary.groups]
                        .filter(g => g.success)
                        .filter(g => !searchTerm || g.name?.toLowerCase().includes(searchTerm.toLowerCase()))
                        .sort((a, b) => (b.value || 0) - (a.value || 0))}
                      onRowClick={(row) => openDetailModal(row, 'investCash')}
                      generateRowId={generateRowId}
                      emptyMessage="No Account Families need to invest cash"
                    />
                  </div>
                </div>
                {errors.investCash && (
                  <div className="card-footer bg-success-subtle text-success">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {errors.investCash}
                  </div>
                )}
              </div>
            )}

            {/* MIXED TAB */}
            {activeTab === 'mixed' && mixedSummary?.groups && (
              <div className="card border-warning border-top-0 rounded-top-0">
                <div className="card-header bg-warning text-dark rounded-top-0">
                  <small className="opacity-75">Requires manual review - accounts need both raise and invest</small>
                </div>
                <div className="card-body p-0">
                  <div className="scrollable-table-container">
                    <SortableResizableTable
                      columns={mixedColumns}
                      hiddenPredicate={(item) => hiddenGroups.has(item.name)}
                      data={[...mixedSummary.groups]
                        .filter(g => !searchTerm || g.name?.toLowerCase().includes(searchTerm.toLowerCase()))}
                      onRowClick={(row) => openDetailModal(row, 'mixed')}
                      generateRowId={generateRowId}
                      emptyMessage="No Account Families have mixed cash needs"
                    />
                  </div>
                </div>
                {errors.mixed && (
                  <div className="card-footer bg-warning-subtle text-warning">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {errors.mixed}
                  </div>
                )}
              </div>
            )}

            {/* NO ACTION TAB */}
            {activeTab === 'noAction' && noActionSummary?.groups && (
              <div className="card border-secondary border-top-0 rounded-top-0">
                <div className="card-header bg-secondary text-white rounded-top-0">
                  <small className="opacity-75">All accounts within cash reserve thresholds — click a row to view details or enter manual trades</small>
                </div>
                <div className="card-body p-0">
                  <div className="scrollable-table-container">
                    <SortableResizableTable
                      columns={[
                        { key: 'name', label: 'Account Family', width: '50%' },
                        { key: 'total_accounts', label: 'Accounts', width: '25%' },
                        { key: 'reason', label: 'Status', width: '25%' }
                      ]}
                      data={[...noActionSummary.groups]
                        .filter(g => !searchTerm || g.name?.toLowerCase().includes(searchTerm.toLowerCase()))}
                      expandableRows={false}
                      onRowClick={(row) => openDetailModal(row, 'noAction')}
                      generateRowId={(row) => row.name}
                      emptyMessage="No Account Families in no-action category"
                    />
                  </div>
                </div>
                {errors.noAction && (
                  <div className="card-footer bg-secondary-subtle text-secondary">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {errors.noAction}
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* Inline styles for spin animation */}
      <style>{`
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Account Details Modal */}
      <AccountDetailsModal
        show={showCashReserveModal}
        onClose={closeCashReserveDetails}
        accountNumber={selectedAccountForDetails?.accountNumber}
        accountName={selectedAccountForDetails?.accountName}
        hideDollarValues={false}
      />

      {/* Account Family Detail Modal */}
      {showDetailModal && selectedGroupForDetail && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
          <div className="modal-dialog modal-xl modal-dialog-scrollable" style={{ maxWidth: '95%' }}>
            <div className="modal-content">
              <div className="modal-header" style={{
                backgroundColor: selectedGroupCategory === 'raiseCash' ? '#dc3545' :
                  selectedGroupCategory === 'investCash' ? '#198754' :
                    selectedGroupCategory === 'noAction' ? '#6c757d' : '#ffc107',
                color: selectedGroupCategory === 'mixed' ? '#212529' : 'white'
              }}>
                <h5 className="modal-title d-flex align-items-center gap-3">
                  <span>
                    <i className={`bi ${selectedGroupCategory === 'raiseCash' ? 'bi-arrow-up-circle' :
                      selectedGroupCategory === 'investCash' ? 'bi-arrow-down-circle' :
                        selectedGroupCategory === 'noAction' ? 'bi-check-circle' :
                          'bi-exclamation-triangle'} me-2`}></i>
                    {selectedGroupForDetail?.name}
                  </span>
                  {/* Strategy + Margin toggles in modal header */}
                  {(() => {
                    const detail = clientDetails[selectedGroupCategory]?.[selectedGroupForDetail?.name];
                    if (!detail) return null;

                    const isMixedHeader = selectedGroupCategory === 'mixed';
                    const isRaiseCash = selectedGroupCategory === 'raiseCash';
                    const isInvestCash = selectedGroupCategory === 'investCash';

                    // For no-action groups, only show the ignore-margin toggle (if applicable)
                    if (selectedGroupCategory === 'noAction') {
                      const hasNoActionMargin = (detail?.account_allocations || []).some(a => (a.marginCashUsed || 0) > 0);
                      if (!hasNoActionMargin) return null;
                      return (
                        <div className="btn-group btn-group-sm" role="group" aria-label="Margin counting mode">
                          <button
                            type="button"
                            className={`btn ${ignoreMargin ? 'btn-light' : 'btn-outline-light'}`}
                            onClick={() => setIgnoreMargin(true)}
                            title="Ignore margin debt — show cash position as if no margin used"
                          >
                            <i className="bi bi-eye-slash me-1"></i>Ignore Margin
                          </button>
                          <button
                            type="button"
                            className={`btn ${!ignoreMargin ? 'btn-light' : 'btn-outline-light'}`}
                            onClick={() => setIgnoreMargin(false)}
                            title="Subtract margin debt from Total Liquid and Reserve Status"
                          >
                            <i className="bi bi-exclamation-triangle me-1"></i>Count Margin
                          </button>
                        </div>
                      );
                    }

                    const hasStrategies = (isMixedHeader || isRaiseCash)
                      && (detail?.raise_cash_result?.strategies || detail?.raise_cash_margin_result?.strategies || detail?.strategies);

                    const hasMarginData = isRaiseCash
                      ? !!detail.raise_cash_margin_result
                      : isInvestCash
                        ? !!detail.invest_cash_margin_result
                        : !!(detail.raise_cash_margin_result || detail.invest_cash_margin_result);

                    return (
                      <div className="d-flex align-items-center gap-2">
                        {/* Sell strategy toggle (raise cash only) */}
                        {hasStrategies && (
                          <div className="btn-group btn-group-sm" role="group" aria-label="Sell strategy">
                            <button
                              type="button"
                              className={`btn ${sellStrategy === 'tax_efficient' ? 'btn-light' : 'btn-outline-light'}`}
                              onClick={() => setSellStrategy('tax_efficient')}
                              title="Prioritizes selling by security type based on account type (tax efficiency)"
                            >
                              <i className="bi bi-piggy-bank me-1"></i>
                              Tax
                            </button>
                            <button
                              type="button"
                              className={`btn ${sellStrategy === 'weight_optimized' ? 'btn-light' : 'btn-outline-light'}`}
                              onClick={() => setSellStrategy('weight_optimized')}
                              title="Prioritizes selling the most overweight positions first"
                            >
                              <i className="bi bi-bar-chart me-1"></i>
                              Weight
                            </button>
                            <button
                              type="button"
                              className={`btn ${sellStrategy === 'isaac' ? 'btn-light' : 'btn-outline-light'}`}
                              onClick={() => setSellStrategy('isaac')}
                              title="Isaac: spec-compliant sleeve-based logic (sleeve priority order, midpoint floors, 80% retry, manual review flag)"
                            >
                              <i className="bi bi-layers me-1"></i>
                              Isaac
                            </button>
                          </div>
                        )}
                        {/* Margin toggle */}
                        {hasMarginData && (
                          <div className="btn-group btn-group-sm" role="group" aria-label="Margin mode">
                            <button
                              type="button"
                              className={`btn ${marginMode === 'no_margin' ? 'btn-light' : 'btn-outline-light'}`}
                              onClick={() => { setMarginMode('no_margin'); setIgnoreMargin(true); }}
                              title="Use full cash balance (margin debt not subtracted)"
                            >
                              <i className="bi bi-eye-slash me-1"></i>
                              Ignore Margin
                            </button>
                            <button
                              type="button"
                              className={`btn ${marginMode === 'with_margin' ? 'btn-light' : 'btn-outline-light'}`}
                              onClick={() => { setMarginMode('with_margin'); setIgnoreMargin(false); }}
                              title="Subtract margin debit balance from available cash"
                            >
                              <i className="bi bi-exclamation-triangle me-1"></i>
                              Count Margin
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </h5>
                <button type="button" className="btn-close" onClick={closeDetailModal}
                  style={{ filter: (selectedGroupCategory === 'mixed') ? 'none' : 'invert(1)' }}></button>
              </div>
              <div className="modal-body">
                {renderDetailContent(
                  selectedGroupCategory,
                  selectedGroupCategory === 'raiseCash' ? 'sell' :
                    selectedGroupCategory === 'investCash' ? 'buy' :
                      selectedGroupCategory === 'noAction' ? 'buy' : 'mixed'
                )}
              </div>
              <div className="modal-footer d-flex justify-content-between">
                <div>
                  {queuedTrades[selectedGroupForDetail?.name] && (
                    <div className="btn-group" role="group">
                      <button
                        type="button"
                        className={`btn btn-sm ${showQueuedTrades ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setShowQueuedTrades(true)}
                        title="Show queued trades"
                      >
                        <i className="bi bi-clock-history me-1"></i>
                        Queued
                      </button>
                      <button
                        type="button"
                        className={`btn btn-sm ${!showQueuedTrades ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setShowQueuedTrades(false)}
                        title="Show suggested trades"
                      >
                        <i className="bi bi-lightbulb me-1"></i>
                        Suggested
                      </button>
                    </div>
                  )}
                </div>
                <div className="d-flex gap-2">
                  {/* Per-group price refresh */}
                  <RefreshPricesButton
                    mode="group"
                    groupName={selectedGroupForDetail?.name}
                    size="sm"
                    onRefreshComplete={(result) => {
                      // Inject the realtime rebalance result directly into clientDetails
                      // instead of re-fetching from S3 (which has stale data)
                      if (selectedGroupForDetail && selectedGroupCategory && result?.rebalance_result) {
                        const rebalResult = result.rebalance_result;
                        // Add a flag so the UI knows these are realtime prices
                        rebalResult._realtimePrices = true;
                        rebalResult._pricesFetched = result.symbols_fetched;
                        rebalResult._fetchedAt = result.fetched_at;

                        setClientDetails(prev => ({
                          ...prev,
                          [selectedGroupCategory]: {
                            ...prev[selectedGroupCategory],
                            [selectedGroupForDetail.name]: rebalResult
                          }
                        }));
                      }
                      // Also refresh summaries
                      fetchAllSummaries();
                    }}
                  />
                  {queuedTrades[selectedGroupForDetail?.name] && (
                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      onClick={async () => {
                        if (!window.confirm(`Clear queued trades for ${selectedGroupForDetail?.name}?`)) return;
                        try {
                          await handleClearQueuedTrades(selectedGroupForDetail?.name);
                        } catch (err) {
                          // handler already alerts on error
                        }
                      }}
                    >
                      <i className="bi bi-trash me-1"></i>
                      Clear Queued
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={() => {
                      if (queueTradesTriggerRef.current) {
                        queueTradesTriggerRef.current();
                      }
                    }}
                    disabled={isQueuingTrades}
                  >
                    {isQueuingTrades ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Queuing...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-clock-fill me-2"></i>
                        Queue Trades
                      </>
                    )}
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={closeDetailModal}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RebalancerDashboard;
